<script setup>
import ActionButton from '@js/components/buttons/ActionButton.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFieldGroup from '@js/components/forms/FormFieldGroup.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormTitle from '@js/components/forms/FormTitle.vue';

const { definition, variable, productAttributes } = defineProps({
    definition: {
        type: Object,
        required: true,
    },
    variable: {
        type: Object,
        required: true,
    },
    productAttributes: {
        type: Array,
        required: true,
    },
});

const periodOptions = [
    { value: 'current', label: 'Mois récents (M-x)' },
    { value: 'lastYearBefore', label: 'Année préc. avant (A-1 M-x)' },
    { value: 'lastYearAfter', label: 'Année préc. après (A-1 M+x)' },
];

const operatorOptions = [
    { value: '>=', label: '≥' },
    { value: '>', label: '>' },
    { value: '<=', label: '≤' },
    { value: '<', label: '<' },
    { value: '=', label: '=' },
];

const logicalOperatorOptions = [
    { value: 'and', label: 'ET' },
    { value: 'or', label: 'OU' },
];

function periodLabel(value) {
    return periodOptions.find((option) => option.value === value)?.label ?? '';
}

function operatorLabel(value) {
    return (
        operatorOptions.find((option) => option.value === value)?.label ?? ''
    );
}

function logicalOperatorLabel(value) {
    return (
        logicalOperatorOptions.find((option) => option.value === value)
            ?.label ?? ''
    );
}

function addCondition() {
    definition.conditions.push({
        metric: 'quantitySold',
        period: 'current',
        months: 6,
        operator: '>=',
        value: null,
        logicalOperator: 'and',
    });
}

function removeCondition(index) {
    definition.conditions.splice(index, 1);
}
</script>

<template>
    <FormContent>
        <div class="flex flex-col gap-2">
            <FormTitle>{{ variable.label }}</FormTitle>

            <p class="text-sm text-gray-400">
                Les conditions ci-dessous définissent la variable
                <span class="font-mono text-gray-600"
                    >${{ variable.value }}</span
                >
                (booléen), utilisable dans le mapping. Par exemple :
                <span class="font-mono text-gray-600"
                    >${{ variable.value }} ? "1" : "0"</span
                >.
            </p>
        </div>
    </FormContent>

    <FormContent>
        <div class="mb-2 flex flex-col gap-4">
            <div class="flex items-center justify-between">
                <FormTitle> Conditions sur la quantité vendue </FormTitle>

                <ActionButton
                    type="secondary"
                    size="small"
                    :callback="addCondition"
                >
                    + Ajouter une condition
                </ActionButton>
            </div>

            <p
                v-if="definition.conditions.length === 0"
                class="text-sm text-gray-400"
            >
                Aucune condition. Ajoutez-en une pour restreindre selon les
                ventes. Les conditions sont évaluées de haut en bas, en
                respectant les opérateurs logiques.
            </p>

            <template
                v-for="(condition, index) in definition.conditions"
                :key="index"
            >
                <div class="flex flex-col gap-2">
                    <ListboxField
                        v-if="index > 0"
                        v-model="condition.logicalOperator"
                        :field-id="`${variable.value}Conditions${index}LogicalOperator`"
                        :field-name="`${variable.value}Conditions${index}LogicalOperator`"
                        label="Opérateur logique"
                        :options="logicalOperatorOptions"
                        option-key="value"
                        size="small"
                    >
                        <template #buttonContent>
                            {{
                                logicalOperatorLabel(condition.logicalOperator)
                            }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <div
                        class="flex flex-wrap items-start gap-4 rounded-sm border border-gray-200 p-4"
                    >
                        <div class="grow">
                            <ListboxField
                                v-model="condition.period"
                                :field-id="`${variable.value}Conditions${index}Period`"
                                :field-name="`${variable.value}Conditions${index}Period`"
                                label="Période"
                                :options="periodOptions"
                                option-key="value"
                                size="small"
                            >
                                <template #buttonContent>
                                    {{ periodLabel(condition.period) }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>
                        </div>

                        <InputField
                            v-model="condition.months"
                            :field-id="`${variable.value}Conditions${index}Months`"
                            :field-name="`${variable.value}Conditions${index}Months`"
                            label="Nombre de mois"
                            type="number"
                            min="1"
                            step="1"
                            size="small"
                        />

                        <ListboxField
                            v-model="condition.operator"
                            :field-id="`${variable.value}Conditions${index}Operator`"
                            :field-name="`${variable.value}Conditions${index}Operator`"
                            label="Opérateur"
                            :options="operatorOptions"
                            option-key="value"
                            size="small"
                        >
                            <template #buttonContent>
                                {{ operatorLabel(condition.operator) }}
                            </template>

                            <template #optionContent="{ option }">
                                {{ option.label }}
                            </template>
                        </ListboxField>

                        <InputField
                            v-model="condition.value"
                            :field-id="`${variable.value}Conditions${index}Value`"
                            :field-name="`${variable.value}Conditions${index}Value`"
                            label="Quantité vendue"
                            type="number"
                            min="0"
                            step="1"
                            size="small"
                        />

                        <div class="flex flex-col">
                            <span class="mb-2 text-sm">&nbsp;</span>

                            <ActionButton
                                type="danger"
                                size="small"
                                :callback="() => removeCondition(index)"
                            >
                                Supprimer
                            </ActionButton>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </FormContent>

    <FormContent>
        <div class="mb-2 flex flex-col gap-4">
            <FormTitle> Condition sur la quantité en stock </FormTitle>

            <FormFieldGroup>
                <ListboxField
                    v-model="definition.quantityInStock.operator"
                    :field-id="`${variable.value}QuantityInStockOperator`"
                    :field-name="`${variable.value}QuantityInStockOperator`"
                    label="Opérateur"
                    :options="operatorOptions"
                    option-key="value"
                    size="small"
                >
                    <template #buttonContent>
                        {{ operatorLabel(definition.quantityInStock.operator) }}
                    </template>

                    <template #optionContent="{ option }">
                        {{ option.label }}
                    </template>
                </ListboxField>

                <InputField
                    v-model="definition.quantityInStock.value"
                    :field-id="`${variable.value}QuantityInStockValue`"
                    :field-name="`${variable.value}QuantityInStockValue`"
                    label="Quantité en stock"
                    type="number"
                    min="0"
                    step="1"
                    size="small"
                />
            </FormFieldGroup>
        </div>
    </FormContent>

    <FormContent>
        <FormTitle> Condition sur les attributs produits </FormTitle>

        <FormGrid
            v-if="productAttributes && productAttributes.length > 0"
            :columns="2"
        >
            <template
                v-for="(productAttribute, index) in productAttributes"
                :key="index"
            >
                <ListboxField
                    v-if="productAttribute.valueType === 'option'"
                    v-model="
                        definition.productAttributeFilters[
                            productAttribute.code
                        ]
                    "
                    :field-id="`${variable.value}ProductAttributeFilters${productAttribute.code}`"
                    :field-name="`${variable.value}ProductAttributeFilters${productAttribute.code}`"
                    :label="productAttribute.label"
                    :options="productAttribute.options"
                    has-empty-option
                    multiple
                    by="value"
                >
                    <template #buttonContent>
                        <template
                            v-if="
                                definition.productAttributeFilters[
                                    productAttribute.code
                                ] &&
                                definition.productAttributeFilters[
                                    productAttribute.code
                                ].length > 0
                            "
                        >
                            {{
                                definition.productAttributeFilters[
                                    productAttribute.code
                                ]
                                    .map((item) => {
                                        return item.label;
                                    })
                                    .join(', ')
                            }}
                        </template>

                        <template v-else> &nbsp; </template>
                    </template>

                    <template #optionContent="{ option }">
                        {{ option.label }}
                    </template>
                </ListboxField>
            </template>
        </FormGrid>
    </FormContent>
</template>
